import { api } from './client';

/*  Specchio dei contratti di TeraWorks.Application.Scontrini (22/10/2026).  */

export interface ArticoloScontrinabile {
  codice: string;
  descrizione: string;
  /** Il prezzo proposto, IVA compresa; null quando il listino di Mago non ce l'ha. */
  prezzo: number | null;
}

export interface PreparazioneScontrino {
  articoli: ArticoloScontrinabile[];
  aliquotaIva: number;
  limiteContanti: number;
  mailTecnico: string | null;
  siPuoEmettere: boolean;
  avviso: string | null;
}

export type Pagamento = 'Contanti' | 'Pos';

export interface RigaNuovoScontrino {
  codiceArticolo: string;
  quantita: number;
  /** IVA compresa; se manca vale quello del listino. */
  prezzoUnitario?: number | null;
}

export interface NuovoScontrino {
  righe: RigaNuovoScontrino[];
  pagamento: Pagamento;
  mailCliente?: string | null;
}

export interface RigaScontrinoLetta {
  codiceArticolo: string;
  descrizione: string;
  quantita: number;
  prezzoUnitario: number;
  /** Il prezzo proposto dal listino: diverso da prezzoUnitario quando il tecnico l'ha cambiato. */
  prezzoListino: number | null;
  importo: number;
}

export type StatoScontrino = 'DaEmettere' | 'Emesso' | 'DaVerificare' | 'Annullato';

export interface DettaglioScontrino {
  id: string;
  numero: string | null;
  stato: StatoScontrino;
  data: string;
  tecnicoId: string;
  tecnico: string;
  pagamento: Pagamento;
  totale: number;
  aliquotaIva: number;
  righe: RigaScontrinoLetta[];
  mailCliente: string | null;
  mailCopia: string | null;
  numeroDocumento: string | null;
  numeroChiusura: string | null;
  emessoIl: string | null;
  tentativi: number;
  ultimoErrore: string | null;
  copiaAccodataIl: string | null;
  motivoAnnullamento: string | null;
  creatoIl: string;
}

export interface IncassoGiorno {
  data: string;
  tecnicoId: string;
  tecnico: string;
  scontrini: number;
  contanti: number;
  pos: number;
}

export interface SituazioneTecnico {
  tecnicoId: string;
  tecnico: string;
  contantiIncassati: number;
  versati: number;
  daVersare: number;
  ultimoVersamentoIl: string | null;
  dallUltimoVersamento: DettaglioScontrino[];
}

export interface MovimentoCassaLetto {
  id: string;
  tipo: 'Versamento' | 'Ritiro';
  tecnicoId: string | null;
  tecnico: string | null;
  importo: number;
  atteso: number;
  note: string | null;
  creatoIl: string;
  creatoDa: string;
}

export interface SituazioneCassa {
  saldo: number;
  ultimoRitiroIl: string | null;
  tecnici: SituazioneTecnico[];
  movimenti: MovimentoCassaLetto[];
  sospesi: DettaglioScontrino[];
}

export interface CorpoVersamento {
  importo: number;
  note?: string | null;
}

export interface CorpoRitiro {
  importo: number;
  note?: string | null;
}

export interface ParametriScontriniLetti {
  articoli: string;
  listinoMago: string | null;
  aliquotaIva: number;
  repartoIva: number;
  limiteContanti: number;
  oraChiusura: string;
  minutiAttornoChiusura: number;
  codiceSede: string;
  contoCassa: string | null;
  contoPosTransitorio: string | null;
  contoPos: string | null;
  modoCopia: ModoCopia;
  stampante: string | null;
}

/** Come arriva il documento al cliente (vedi ParametriScontrini.cs). */
export type ModoCopia = 'CopiaTeraWorks' | 'ScontrinoDigitale';

export interface EsitoVerificaScontrino {
  scontrino: DettaglioScontrino;
  messaggio: string;
}

export interface ParametriScontriniDaScrivere {
  articoli: string;
  listinoMago: string | null;
  aliquotaIva: number;
  repartoIva: number;
  limiteContanti: number;
  oraChiusura: string;
  minutiAttornoChiusura: number;
  codiceSede: string;
  contoCassa: string | null;
  contoPosTransitorio: string | null;
  contoPos: string | null;
  modoCopia: ModoCopia;
}

const periodo = (dal?: string, al?: string) => {
  const p = new URLSearchParams();
  if (dal) p.set('dal', dal);
  if (al) p.set('al', al);
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const scontrini = {
  prepara: () => api.get<PreparazioneScontrino>('/scontrini/prepara'),
  crea: (nuovo: NuovoScontrino) => api.post<DettaglioScontrino>('/scontrini', nuovo),
  miei: (dal?: string, al?: string) => api.get<DettaglioScontrino[]>(`/scontrini/miei${periodo(dal, al)}`),
  tutti: (dal?: string, al?: string) => api.get<DettaglioScontrino[]>(`/scontrini${periodo(dal, al)}`),
  incassi: (dal?: string, al?: string) => api.get<IncassoGiorno[]>(`/scontrini/incassi${periodo(dal, al)}`),
  leggi: (id: string) => api.get<DettaglioScontrino>(`/scontrini/${id}`),
  riprova: (id: string) => api.post<DettaglioScontrino>(`/scontrini/${id}/riprova`, {}),
  uscito: (id: string, numeroDocumento: string | null) =>
    api.post<DettaglioScontrino>(`/scontrini/${id}/uscito`, { numeroDocumento }),
  verifica: (id: string) => api.post<EsitoVerificaScontrino>(`/scontrini/${id}/verifica`, {}),
  nonUscito: (id: string) => api.post<DettaglioScontrino>(`/scontrini/${id}/non-uscito`, {}),
  annulla: (id: string, motivo: string) => api.post<DettaglioScontrino>(`/scontrini/${id}/annulla`, { motivo }),
};

export const cassa = {
  mia: () => api.get<SituazioneTecnico>('/cassa/mia'),
  versa: (corpo: CorpoVersamento) => api.post<SituazioneTecnico>('/cassa/versa', corpo),
  versaPer: (tecnicoId: string, corpo: CorpoVersamento) =>
    api.post<SituazioneTecnico>(`/cassa/versa/${tecnicoId}`, corpo),
  situazione: () => api.get<SituazioneCassa>('/cassa'),
  ritira: (corpo: CorpoRitiro) => api.post<SituazioneCassa>('/cassa/ritira', corpo),
  parametri: () => api.get<ParametriScontriniLetti>('/cassa/parametri'),
  scriviParametri: (valori: ParametriScontriniDaScrivere) =>
    api.put<ParametriScontriniLetti>('/cassa/parametri', valori),
};
